import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';

// Import food and interior images
import food1 from '../assets/food/image00005.jpeg';
import food2 from '../assets/food/image00003.jpeg';
import food3 from '../assets/food/image00033.jpeg';
import food4 from '../assets/food/image00032.jpeg';
import food5 from '../assets/food/image00020.jpeg';
import food6 from '../assets/food/image00012.jpeg';
import interior1 from '../assets/interior/image00018.jpeg';
import interior2 from '../assets/interior/image00019.jpeg';
import interior3 from '../assets/interior/image00034.jpeg';
import interior4 from '../assets/interior/image00035.jpeg';

const GallerySection = styled.section`
  padding: 100px 0 120px;
  background-color: ${theme.colors.light};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(to right, ${theme.colors.burgundy}, ${theme.colors.primary}, ${theme.colors.slate});
  }
  
  @media (max-width: 768px) {
    padding: 80px 0 100px;
  }
  
  @media (max-width: 480px) {
    padding: 60px 0 80px;
  }
`;

const GalleryTitle = styled.h2`
  font-family: ${theme.fonts.heading};
  font-size: 3.25rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  text-align: center;
  margin-bottom: 20px;
  letter-spacing: 1px;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.caramel};
    margin: 15px auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.3rem;
    
    &::after {
      width: 60px;
      margin: 12px auto 0;
    }
  }
`;

const GallerySubtitle = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 1.2rem;
  color: ${theme.colors.taupe};
  text-align: center;
  margin-bottom: 60px;
  font-style: italic;
  letter-spacing: 0.5px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 50px;
    max-width: 90%;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 40px;
    letter-spacing: 0.3px;
  }
`;

const CategoryTitle = styled.h3`
  font-family: ${theme.fonts.heading};
  font-size: 2.2rem;
  font-weight: 600;
  color: ${theme.colors.secondary};
  text-align: center;
  margin: 40px 0 30px;
  letter-spacing: 0.5px;
  position: relative;
  
  &::before, &::after {
    content: '';
    display: inline-block;
    width: 40px;
    height: 1px;
    background-color: ${theme.colors.burgundy};
    vertical-align: middle;
    margin: 0 15px;
    opacity: 0.7;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin: 35px 0 25px;
    
    &::before, &::after {
      width: 30px;
      margin: 0 10px;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin: 30px 0 20px;
    
    &::before, &::after {
      width: 25px;
      margin: 0 8px;
    }
  }
`;

const CarouselContainer = styled.div`
  max-width: 1200px;
  width: 95%;
  margin: 0 auto 100px;
  position: relative;
  padding: 0 0 20px;
  overflow: hidden;
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.large};
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(to right, transparent, ${theme.colors.taupe}, transparent);
    opacity: 0.3;
  }
  
  @media (max-width: 768px) {
    margin: 0 auto 80px;
  }
  
  @media (max-width: 480px) {
    margin: 0 auto 60px;
    width: 92%;
    border-radius: ${theme.borderRadius.small};
  }
`;

const CarouselTrack = styled.div`
  display: flex;
  transition: transform 0.7s cubic-bezier(0.45, 0, 0.2, 1);
  width: 100%;
  height: 70vh;
  max-height: 700px;
  will-change: transform;
  transform-style: preserve-3d;
  backface-visibility: hidden;
  transform: translate3d(0, 0, 0);
  
  @media (max-width: 768px) {
    height: 50vh;
  }
`;

const CarouselSlide = styled.div`
  min-width: 100%;
  flex: 0 0 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #1a1a1a;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 80%, rgba(0,0,0,0.2) 100%);
    pointer-events: none;
    z-index: 2;
  }
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.02);
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(61, 54, 53, 0.8);
  color: ${theme.colors.light};
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.8rem;
  cursor: pointer;
  z-index: 5;
  transition: all 0.3s ease;
  box-shadow: ${theme.shadows.medium};
  
  &:hover {
    background-color: ${theme.colors.burgundy};
  }
  
  &:focus {
    outline: none;
  }
  
  &:first-of-type {
    left: 20px;
  }
  
  &:last-of-type {
    right: 20px;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.5rem;
    
    &:first-of-type {
      left: 10px;
    }
    
    &:last-of-type {
      right: 10px;
    }
  }
  
  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
    font-size: 1.2rem;
  }
`;

const CarouselIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  
  @media (max-width: 480px) {
    gap: 8px;
    margin-top: 15px;
  }
`;

const CarouselIndicator = styled.button<{ isActive: boolean }>`
  width: ${props => props.isActive ? '30px' : '10px'};
  height: 10px;
  border-radius: 5px;
  background-color: ${props => props.isActive ? theme.colors.burgundy : theme.colors.taupe};
  border: none;
  opacity: ${props => props.isActive ? 1 : 0.5};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    opacity: ${props => props.isActive ? 1 : 0.7};
  }
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 480px) {
    width: ${props => props.isActive ? '25px' : '8px'};
    height: 8px;
  }
`;

const Lightbox = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const LightboxImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  box-shadow: ${theme.shadows.large};
  border: 2px solid ${theme.colors.light};
  
  @media (max-width: 480px) {
    max-width: 95%;
    max-height: 95%;
    border-width: 1px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(61, 54, 53, 0.6);
  color: ${theme.colors.light};
  font-size: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${theme.colors.burgundy};
  }
  
  @media (max-width: 768px) {
    top: 15px;
    right: 15px;
    width: 35px;
    height: 35px;
    font-size: 1.3rem;
  }
  
  @media (max-width: 480px) {
    top: 10px;
    right: 10px;
    width: 30px;
    height: 30px;
    font-size: 1.1rem;
  }
`;

interface CarouselProps {
  images: { src: string; alt: string }[];
  onImageClick: (src: string) => void;
}

const Carousel: React.FC<CarouselProps> = ({ images, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(prevIndex =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
    setTimeout(() => setIsAnimating(false), 700);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(prevIndex =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
    setTimeout(() => setIsAnimating(false), 700);
  };

  const goToSlide = (index: number) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 700);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) {
        nextSlide();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length, isAnimating]);

  return (
    <CarouselContainer>
      <CarouselTrack style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
        {images.map((image, index) => (
          <CarouselSlide key={index}>
            <CarouselImage
              src={image.src}
              alt={image.alt}
              onClick={() => onImageClick(image.src)}
            />
          </CarouselSlide>
        ))}
      </CarouselTrack>

      <NavigationButton onClick={(e) => {
        e.stopPropagation();
        prevSlide();
      }}>&#10094;</NavigationButton>
      <NavigationButton onClick={(e) => {
        e.stopPropagation();
        nextSlide();
      }}>&#10095;</NavigationButton>

      <CarouselIndicators>
        {images.map((_, index) => (
          <CarouselIndicator
            key={index}
            isActive={index === currentIndex}
            onClick={() => goToSlide(index)}
          />
        ))}
      </CarouselIndicators>
    </CarouselContainer>
  );
};

const Gallery = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  const foodImages = [
    { src: food1, alt: "Delicious Italian pizza" },
    { src: food2, alt: "Pasta dish" },
    { src: food3, alt: "Italian dessert" },
    { src: food4, alt: "Fresh salad" },
    { src: food5, alt: "Appetizers" },
    { src: food6, alt: "Signature dish" }
  ];

  const interiorImages = [
    { src: interior1, alt: "Restaurant interior" },
    { src: interior2, alt: "Dining area" },
    { src: interior3, alt: "Bar area" },
    { src: interior4, alt: "Private dining room" }
  ];

  const openLightbox = (imageSrc: string) => {
    setSelectedImage(imageSrc);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <GallerySection id="gallery">
      <GalleryTitle>Our Gallery</GalleryTitle>
      <GallerySubtitle>Explore our culinary artistry and elegant atmosphere</GallerySubtitle>

      <CategoryTitle>Our Cuisine</CategoryTitle>
      <Carousel images={foodImages} onImageClick={openLightbox} />

      <CategoryTitle>Our Space</CategoryTitle>
      <Carousel images={interiorImages} onImageClick={openLightbox} />

      {lightboxOpen && (
        <Lightbox onClick={closeLightbox}>
          <CloseButton onClick={closeLightbox}>×</CloseButton>
          <LightboxImage src={selectedImage} />
        </Lightbox>
      )}
    </GallerySection>
  );
};

export default Gallery;
